import { useMemo } from 'react';
import { useRouter } from 'next/router';

/**
 * Utility hook to get the current page URL that works during both SSR and client-side rendering.
 *
 * CONTEXT: This was created to fix a Facebook sharing issue where the wrong URL format was being generated.
 *
 * PROBLEM:
 * - Expected Facebook share URL: https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fartists.spotify.com%2Fblog%2Fsuper-listeners-your-guide-for-developing-fans-who-go-deeper
 * - Actual generated URL: https://www.facebook.com/share_channel/?type=empty&source_surface=external_share
 *
 * ROOT CAUSE:
 * - During server-side rendering (SSR), `window.location.href` is not available
 * - The ShareButtonsComponent was falling back to an empty string or undefined value
 * - This caused the Frodor modules to generate the wrong Facebook share URL format
 *
 * SOLUTION:
 * - Use Next.js router's `asPath` property during SSR to construct the URL
 * - Use `window.location.href` on the client-side for the full URL
 * - This ensures the correct URL is always available for sharing
 *
 * USAGE:
 * - Import this hook in modules that need to share the current page URL
 * - Pass the returned URL to Frodor modules via the `shareUrl` prop
 * - The Frodor modules will then generate the correct Facebook share URL
 *
 * @param providedUrl - Optional URL to use instead of the current page URL
 * @returns The current page URL that works during SSR and client-side
 */
export const useCurrentPageUrl = (providedUrl?: string): string => {
  const router = useRouter();

  return useMemo(() => {
    // If a URL is explicitly provided, use it
    if (providedUrl) {
      return providedUrl;
    }

    // On the client-side, use the full window.location.href
    if (typeof window !== 'undefined') {
      return window.location.href;
    }

    // During SSR, construct the URL from the router's asPath
    // This ensures we have a valid URL even when window.location is not available
    return `https://artists.spotify.com${router.asPath}`;
  }, [providedUrl, router.asPath]);
};
